import type {StyleFunctionProps, SystemStyleObject} from '@chakra-ui/styled-system';
import {mode} from '@chakra-ui/theme-tools';

import {tokens as dark} from '../../../generated/dark';
import {tokens as light} from '../../../generated/light';
import {shadows} from '../../../theme/shadows';
import {buttonTheme} from '../../Button/theme';

import {radioButtonGroupAnatomy} from './anatomy';
import {vars} from './css-vars';

import {
  createMultiStyleConfigHelpers,
  defineStyle,
  defineStyleConfig,
  resolveStyleConfig,
} from '@chakra-ui/styled-system';

const {defineMultiStyleConfig, definePartsStyle} = createMultiStyleConfigHelpers(radioButtonGroupAnatomy.keys());

const tokens = {
  light: light.components[`radio-button-group`],
  dark: dark.components[`radio-button-group`],
};

const semantic = {
  light: {
    color: light.semantic.colors.text.state,
    background: light.semantic.colors.background.state,
    borderColor: light.semantic.colors.stroke.state,
  },
  dark: {
    color: dark.semantic.colors.text.state,
    background: dark.semantic.colors.background.state,
    borderColor: dark.semantic.colors.stroke.state,
  },
};

const group = defineStyle(props => {
  const {orientation = `horizontal`} = props;

  const gapStyles = {
    horizontal: (spacing: any): SystemStyleObject => ({
      '& > *:not(style) ~ *:not(style)': {marginStart: spacing},
    }),
    vertical: (spacing: any): SystemStyleObject => ({
      '& > *:not(style) ~ *:not(style)': {marginTop: spacing},
    }),
  };

  return {
    // Define CSS variables
    [vars.background.variable]: mode(
      `colors.${tokens.light[`background-default`]}`,
      `colors.${tokens.dark[`background-default`]}`,
    )(props),
    [vars.color.variable]: mode(
      `colors.${tokens.light[`text-default`]}`,
      `colors.${tokens.dark[`text-default`]}`,
    )(props),
    [vars[`border-color`].variable]: mode(
      `colors.${tokens.light[`stroke-default`]}`,
      `colors.${tokens.dark[`stroke-default`]}`,
    )(props),
    [vars[`icon-color`].variable]: mode(
      `colors.${tokens.light[`icon-default`]}`,
      `colors.${tokens.dark[`icon-default`]}`,
    )(props),

    [vars.gap.variable]: `0`,
    ...gapStyles[orientation](vars.gap.variable),

    // Apply styles
    color: vars.color.reference,
    boxShadow: shadows.sm,
    borderRadius: `radius-rounded`,

    // Layout
    display: `inline-flex`,
    flexDirection: orientation === `horizontal` ? `row` : `column`,
  };
});

const schemeMap = (props: StyleFunctionProps) => ({
  danger: {
    color: mode(semantic.light.color[`danger-bold`], semantic.dark.color[`danger-bold`])(props),
    bgColor: mode(semantic.light.background[`danger-subtle`], semantic.dark.background[`danger-subtle`])(props),
    borderColor: mode(semantic.light.borderColor[`danger-subtle`], semantic.dark.borderColor[`danger-subtle`])(props),
  },
  warning: {
    color: mode(semantic.light.color[`warning-bold`], semantic.dark.color[`warning-bold`])(props),
    bgColor: mode(semantic.light.background[`warning-subtle`], semantic.dark.background[`warning-subtle`])(props),
    borderColor: mode(semantic.light.borderColor[`warning-subtle`], semantic.dark.borderColor[`warning-subtle`])(props),
  },
  success: {
    color: mode(semantic.light.color[`success-bold`], semantic.dark.color[`success-bold`])(props),
    bgColor: mode(semantic.light.background[`success-subtle`], semantic.dark.background[`success-subtle`])(props),
    borderColor: mode(semantic.light.borderColor[`success-subtle`], semantic.dark.borderColor[`success-subtle`])(props),
  },
});

const item = defineStyle(props => {
  const {orientation = `horizontal`, colorScheme} = props;

  const buttonStyle = resolveStyleConfig(buttonTheme)(props);

  const radius = {
    horizontal: {
      [`&[data-first]`]: {
        borderTopStartRadius: `radius-rounded`,
        borderBottomStartRadius: `radius-rounded`,
      },
      [`&[data-middle]`]: {},
      [`&[data-last]`]: {
        borderTopEndRadius: `radius-rounded`,
        borderBottomEndRadius: `radius-rounded`,
      },
    },
    vertical: {
      [`&[data-first]`]: {
        borderTopStartRadius: `radius-rounded`,
        borderTopEndRadius: `radius-rounded`,
      },
      [`&[data-middle]`]: {},
      [`&[data-last]`]: {
        borderBottomStartRadius: `radius-rounded`,
        borderBottomEndRadius: `radius-rounded`,
      },
    },
  };
  const map = schemeMap(props);

  let schemeProperties = map?.[colorScheme as keyof typeof map] || {
    color: vars.color.reference,
    bgColor: vars.background.reference,
  };

  if (typeof colorScheme === `object`) {
    schemeProperties = colorScheme;
  }

  const disabled = {
    color: mode(`${tokens.light[`text-disabled`]}`, `${tokens.dark[`text-disabled`]}`)(props),
    bgColor: mode(`${tokens.light[`background-disabled`]}`, `${tokens.dark[`background-disabled`]}`)(props),
    borderColor: mode(`${tokens.light[`stroke-default`]}`, `${tokens.dark[`stroke-default`]}`)(props),
  };

  return {
    ...buttonStyle,
    // Layout
    height: `2rem`,
    display: `flex`,
    alignItems: `center`,
    justifyContent: `center`,
    flexShrink: 0,

    // Spacing
    gap: `space-100`,
    px: `space-100`,
    py: `space-200`,

    // Border
    borderWidth: `1px`,
    // Radius
    borderRadius: `0`,
    ...radius[orientation],

    // Color
    // [`&:not([data-disabled])[data-checked]`]: {
    //   ...schemeProperties,
    // },
    // [`&:not(data-disabled)`]: {
    //   ...schemeProperties,
    // },
    [`&[data-disabled]`]: disabled,
    [`&[data-checked]`]: {
      bgColor: schemeProperties.bgColor,
      color: schemeProperties.color,
      borderColor: schemeProperties.color,
    },
    _disabled: disabled,
    _hover: {
      bgColor: mode(`${tokens.light[`background-hover`]}`, `${tokens.dark[`background-hover`]}`)(props),
      borderColor: mode(`${tokens.light[`stroke-default`]}`, `${tokens.dark[`stroke-default`]}`)(props),
      color: vars.color.reference,
      _disabled: disabled,
    },
    _active: {
      bgColor: mode(`${tokens.light[`background-pressed`]}`, `${tokens.dark[`background-pressed`]}`)(props),
      borderColor: mode(`${tokens.light[`stroke-default`]}`, `${tokens.dark[`stroke-default`]}`)(props),
      color: vars.color.reference,
      _disabled: disabled,
    },
    _focus: {
      borderColor: mode(`${tokens.light[`stroke-focus`]}`, `${tokens.dark[`stroke-focus`]}`)(props),
      _disabled: disabled,
    },
  };
});

// We export the item styles to be used in the RadioButton component (for individual color styling)
export const itemStyle = defineStyleConfig({
  baseStyle: props => item(props),
});

export const itemContainer = defineStyle(() => {
  return {
    cursor: `pointer`,

    [`&[data-disabled]`]: {
      cursor: `not-allowed`,
      pointerEvents: `none`,
    },
  };
});

export const itemIcon = defineStyle(props => {
  const {colorScheme} = props;

  const map = schemeMap(props);

  let schemeProperties = map?.[colorScheme as keyof typeof map] || {
    color: vars.color.reference,
    bgColor: vars.background.reference,
  };

  if (typeof colorScheme === `object`) {
    schemeProperties = colorScheme;
  }

  return {
    // Layout
    display: `flex`,
    alignItems: `center`,
    justifyContent: `center`,
    flexShrink: 0,
    gap: `space-100`,
    // Size
    width: `1rem`,
    height: `1rem`,

    borderRadius: `radius-100`,

    [`&[data-hover]`]: {
      color: schemeProperties.color,
      bgColor: schemeProperties.bgColor,
    },
    [`&[data-active]`]: {
      color: schemeProperties.color,
      bgColor: schemeProperties.bgColor,
    },
    [`&[data-focus]`]: {
      color: schemeProperties.color,
      bgColor: schemeProperties.bgColor,
    },
    [`&[data-checked]`]: {
      bgColor: schemeProperties.bgColor,
      color: schemeProperties.color,
    },
    // [`&[data-checked]`]: {
    //   color: schemeProperties.color,
    // },
  };
});

export const itemIconStyles = defineStyleConfig({
  baseStyle: props => itemIcon(props),
});

const baseStyle = definePartsStyle(props => ({
  group: group(props),
  'item-container': itemContainer(),
  item: item(props),
  'item-icon': itemIcon(props),
}));

export const radioButtonGroupTheme: ReturnType<typeof defineMultiStyleConfig> = defineMultiStyleConfig({
  baseStyle,
  defaultProps: {
    // @ts-expect-error: Orientation is not part of default props for some reason
    orientation: `horizontal`,
  },
});
